import React, {useState} from "react";
import {ErrorBanner, Modal} from "../../../components";
import {updateMerchant} from "../../../api";
import type {MerchantResponse, UpdateMerchantRequest} from "../../../api";
import type {RateSource} from "../../../types";
import {formatRateMarkup} from "../../../utils";
import {validateRateMarkup, rateHintExample} from "./rateMarkup";

type Direction = "in" | "out";
type Props = {token: string; merchant: MerchantResponse; onSaved: (merchant: MerchantResponse) => void};

function settings(merchant: MerchantResponse, direction: Direction) {
    return direction === "in"
        ? {source: merchant.inRateSource ?? merchant.rateSource, percent: merchant.inRateSource == null ? merchant.rateMarkupPercent : merchant.inRateMarkupPercent ?? null}
        : {source: merchant.outRateSource ?? merchant.rateSource, percent: merchant.outRateSource == null ? merchant.rateMarkupPercent : merchant.outRateMarkupPercent ?? null};
}

export function MerchantRatesSection({token, merchant, onSaved}: Props): React.ReactElement {
    const [direction, setDirection] = useState<Direction | null>(null);
    const [source, setSource] = useState<RateSource>("manual");
    const [magnitude, setMagnitude] = useState("");
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<unknown>(null);
    const percent = `${direction === "out" && Number(magnitude) !== 0 ? "-" : ""}${magnitude}`;
    const sign = direction === "out" ? "−" : "+";

    const open = (side: Direction) => {
        const current = settings(merchant, side);
        setDirection(side);
        setSource(current.source);
        setMagnitude((current.percent ?? "").replace(/^[+-]/, ""));
        setError(null);
    };

    const save = async () => {
        if (!direction) return;
        if (source === "rapira") {
            if (!/^\d+(?:\.\d{1,2})?$/.test(magnitude) || validateRateMarkup(percent)) {
                setError(new Error(direction === "in" ? "Введите процент от 0 до 999.99, не более двух знаков после точки." : "Введите процент от 0 до 99.99, не более двух знаков после точки."));
                return;
            }
        }
        const body: UpdateMerchantRequest = direction === "in"
            ? {inRateSource: source, inRateMarkupPercent: source === "manual" ? "" : percent}
            : {outRateSource: source, outRateMarkupPercent: source === "manual" ? "" : percent};
        setSaving(true);
        setError(null);
        try {
            onSaved(await updateMerchant(token, merchant.id, body));
            setDirection(null);
        } catch (problem) {setError(problem);} finally {setSaving(false);}
    };

    return <section className="merchant-rate-block">
        <div className="section-header"><h3>Курсы заявок</h3></div>
        <div className="merchant-rate-summary merchant-direction-rates">
            {(["in", "out"] as const).map((side) => {
                const current = settings(merchant, side);
                return <div key={side} data-testid={`merchant-rate-${side}`}>
                    <span className="meta-label">Курс заявок {side.toUpperCase()}</span>
                    <strong>{current.source === "rapira" ? formatRateMarkup(current.percent) : "Вручную"}</strong>
                    <small>{side === "in" ? "Приём RUB · зачисление USDT" : "Выплата RUB · списание USDT"}</small>
                    {current.source === "rapira" && <small>{side === "in" ? "Процент повышает курс покупки USDT" : "Процент снижает курс выплаты RUB"}</small>}
                    <small>{current.source === "rapira" ? "Rapira" : "Курс вводит трейдер"}</small>
                    <button type="button" className="btn btn-ghost btn-sm" onClick={() => open(side)}>Настроить {side.toUpperCase()}</button>
                </div>;
            })}
        </div>
        <p className="section-hint merchant-rate-footnote">IN и OUT настраиваются независимо. Уже созданные заявки сохраняют свой курс.</p>
        <Modal open={direction !== null} title={`Курс заявок ${direction?.toUpperCase() ?? ""}`} onClose={() => {if (!saving) setDirection(null);}} sizeClassName="modal-sm">
            <ErrorBanner error={error}/>
            <div className="form-group">
                <label htmlFor="rate-source">Источник курса</label>
                <select id="rate-source" value={source} disabled={saving} onChange={(e) => setSource(e.target.value as RateSource)}>
                    <option value="manual">Вручную (трейдер вводит)</option><option value="rapira">Rapira</option>
                </select>
            </div>
            {source === "rapira" && <div className="form-group">
                <label htmlFor="rate-markup-percent">Процент {direction?.toUpperCase()}</label>
                <div className="merchant-rate-input"><span aria-hidden="true">{sign}</span>
                    <input id="rate-markup-percent" type="text" inputMode="decimal" value={magnitude} disabled={saving}
                        onChange={(e) => setMagnitude(e.target.value.replace(/^[+-]/, ""))}
                        placeholder="Например, 2.5" aria-describedby="rate-markup-hint"/>
                    <span aria-hidden="true">%</span>
                </div>
                <small id="rate-markup-hint">{direction === "in" ? "Плюс к курсу биржи применяется автоматически." : "Минус от курса биржи применяется автоматически."}</small>
                <small>{rateHintExample(percent)}</small>
            </div>}
            <div className="modal-actions">
                <button type="button" className="btn btn-ghost" disabled={saving} onClick={() => setDirection(null)}>Отмена</button>
                <button type="button" className="btn btn-primary" disabled={saving} onClick={() => void save()}>{saving ? "Сохранение…" : "Сохранить"}</button>
            </div>
        </Modal>
    </section>;
}
